import { Pencil, Plus, Tag, Trash2 } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { type AuditStatusConfig, useAuditStatusConfigs } from "@/hooks/use-audit-status-configs";
import { CreateAuditStatusDialog } from "./CreateAuditStatusDialog";
import { DeleteAuditStatusDialog } from "./DeleteAuditStatusDialog";
import { EditAuditStatusDialog } from "./EditAuditStatusDialog";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; config: AuditStatusConfig }
  | { type: "delete"; config: AuditStatusConfig };

export function AuditStatusesTable() {
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  const { data: configs = [], isLoading, isError, error, refetch } = useAuditStatusConfigs();

  function closeDialog() {
    setDialog({ type: "none" });
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load statuses"} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground max-w-prose">
          Configure the statuses used to track audit progress. System statuses are built-in and
          cannot be deleted, but their label and color can be customized.
        </p>
        <Button onClick={() => setDialog({ type: "create" })} className="shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Add Status
        </Button>
      </div>

      <DataTable
        id="audit-statuses"
        headers={[
          { key: "swatch", label: "", defaultWidth: 48, minWidth: 48 },
          { key: "label", label: "Label", defaultWidth: 160 },
          { key: "key", label: "Key", className: "hidden md:table-cell", defaultWidth: 160 },
          { key: "type", label: "Type", className: "hidden sm:table-cell", defaultWidth: 100 },
          { key: "actions", label: "Actions", className: "text-right", defaultWidth: 110 },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Tag}
            title="No statuses"
            description="Add a custom status to get started."
          />
        }
      >
        {configs.map((config) => (
          <tr
            key={config.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 w-10">
              <div
                className="h-3.5 w-3.5 rounded-full ring-1 ring-black/10"
                style={{ backgroundColor: config.color }}
              />
            </td>
            <td className="px-4 py-3 font-medium">{config.label}</td>
            <td className="hidden md:table-cell px-4 py-3 font-mono text-xs text-muted-foreground">
              {config.key}
            </td>
            <td className="hidden sm:table-cell px-4 py-3">
              {config.isSystem ? (
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  System
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  Custom
                </span>
              )}
            </td>
            <td className="px-4 py-3 text-right">
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDialog({ type: "edit", config })}
                >
                  <Pencil className="h-4 w-4" />
                  <span className="sr-only">Edit</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={config.isSystem}
                  onClick={() => setDialog({ type: "delete", config })}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Delete</span>
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </DataTable>

      <CreateAuditStatusDialog
        open={dialog.type === "create"}
        onOpenChange={(open) => !open && closeDialog()}
      />

      {dialog.type === "edit" && (
        <EditAuditStatusDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          config={dialog.config}
        />
      )}

      {dialog.type === "delete" && (
        <DeleteAuditStatusDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          config={dialog.config}
        />
      )}
    </div>
  );
}
